import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaFacebook, FaInstagram, FaTiktok, FaPinterest, FaTwitter,
  FaCcVisa, FaCcMastercard, FaCcPaypal, FaApplePay, FaGooglePay,
  FaArrowRight, FaMapMarkerAlt, FaEnvelope, FaPhone, FaWhatsapp,
  FaTimes,
} from 'react-icons/fa';
import logo from '../assets/logo.jpeg';
import { useWhatsAppSettings } from '../hooks/useWhatsAppSettings';
import { useSiteContent } from '../hooks/useSiteContent';
import { getCurrentProduct } from '../hooks/currentProductStore';
import { getCurrentService } from '../hooks/currentServiceStore';
import './Footer.css';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const location = useLocation();
  const { openWhatsApp, settings, hasNumber, buildServiceMessage } = useWhatsAppSettings();
  const { content } = useSiteContent();
  const { footer, social } = content;
  const services = content.services || [];

  // Don't show the floating button on admin pages
  const isAdmin = location.pathname.startsWith('/admin');

  // Build a context-aware message for the floating bubble.
  // If the visitor is on a product page, the conversation opens about that product.
  const getFloatingMessage = () => {
    const product = getCurrentProduct();
    if (product) {
      const price = product.discountPrice || product.price;
      return [
        `Hi! I'm looking at *${product.name}*${price ? ` (${price > 0 ? `KSh ${Number(price).toFixed(0)}` : ''})` : ''} on your website.`,
        product.description
          ? `\n\n${product.description.slice(0, 180)}${product.description.length > 180 ? '…' : ''}`
          : '',
        `\n\nCould you help me with more details or availability? 😊`,
      ].join('');
    }
    const service = getCurrentService();
    if (service) return buildServiceMessage(service.title);
    return settings.footerMessage;
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
    }
  };

  const serviceLinks = services.map((s) => ({ label: s.title, to: `/services/${s.slug}` }));

  const shopLinks = [
    { label: 'All Products',  to: '/shop' },
    { label: 'New Arrivals',  to: '/shop?sort=newest' },
    { label: 'Categories',    to: '/categories' },
    { label: 'Brands',        to: '/brands' },
    { label: 'Special Offers', to: '/shop?sort=discount' },
    { label: 'Track My Order', to: '/orders' },
    { label: 'My Wishlist',    to: '/wishlist' },
  ];

  const companyLinks = [
    { label: 'About Us',     to: '/about' },
    { label: 'All Services', to: '/services' },
    { label: 'Request a Quote', to: '/contact' },
    { label: 'Contact Us',   to: '/contact' },
  ];

  const socials = [
    { icon: <FaInstagram />, href: social.instagram, label: 'Instagram' },
    { icon: <FaTiktok />,    href: social.tiktok,    label: 'TikTok' },
    { icon: <FaFacebook />,  href: social.facebook,  label: 'Facebook' },
    { icon: <FaPinterest />, href: social.pinterest, label: 'Pinterest' },
    { icon: <FaTwitter />,   href: social.twitter,   label: 'Twitter' },
  ].filter(s => s.href);

  return (
    <>
    {/* ── Floating WhatsApp bubble ── */}
    {hasNumber && !isAdmin && (
      <div className="wa-float-wrap">
        {showTooltip && (() => {
          const product = getCurrentProduct();
          const service = getCurrentService();
          return (
            <div className="wa-float-tooltip">
              <button className="wa-float-tooltip-close" onClick={() => setShowTooltip(false)} aria-label="Close">
                <FaTimes />
              </button>
              <p>
                {product
                  ? `👋 Have a question about this product?`
                  : service
                    ? `👋 Want to know more about this service?`
                    : `👋 Hi! Chat with us on WhatsApp`}
              </p>
              {product && (
                <p className="wa-float-product-name">{product.name}</p>
              )}
              {!product && service && (
                <p className="wa-float-product-name">{service.title}</p>
              )}
              <button
                className="wa-float-tooltip-btn"
                onClick={() => { openWhatsApp(getFloatingMessage()); setShowTooltip(false); }}
              >
                <FaWhatsapp /> {product ? 'Ask about this product' : service ? 'Ask about this service' : 'Start Chat'}
              </button>
            </div>
          );
        })()}
        <button
          className="wa-float-btn"
          onClick={() => setShowTooltip((v) => !v)}
          aria-label="Chat on WhatsApp"
          title="Chat with us on WhatsApp"
        >
          {showTooltip ? <FaTimes /> : <FaWhatsapp />}
        </button>
      </div>
    )}

    <footer className="footer">
      {/* Wave top decoration */}
      <div className="footer-wave">
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#1a1a2e" />
        </svg>
      </div>

      <div className="footer-body">
        <div className="footer-container">

          {/* ── Row 1: Brand + Columns + Newsletter ── */}
          <div className="footer-grid">

            {/* Brand */}
            <div className="footer-brand-col">
              <Link to="/" className="footer-logo-link">
                <img src={logo} alt="Follex Tech" className="footer-logo-img" />
              </Link>
              <p className="footer-tagline">
                {footer.tagline}
              </p>

              <div className="footer-contact">
                {footer.email && (
                  <a href={`mailto:${footer.email}`} className="footer-contact-item">
                    <FaEnvelope /> {footer.email}
                  </a>
                )}
                {footer.phone && (
                  <a href={`tel:${footer.phone.replace(/\s+/g, '')}`} className="footer-contact-item">
                    <FaPhone /> {footer.phone}
                  </a>
                )}
                {footer.location && (
                  <span className="footer-contact-item">
                    <FaMapMarkerAlt /> {footer.location}
                  </span>
                )}
              </div>

              <div className="footer-socials">
                {socials.map((s) => (
                  <a key={s.label} href={s.href} className="footer-social-btn" aria-label={s.label} target="_blank" rel="noreferrer">
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Service links */}
            <div className="footer-links-col">
              <h5 className="footer-col-title">Services</h5>
              <ul>
                {serviceLinks.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="footer-link">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Shop links */}
            <div className="footer-links-col">
              <h5 className="footer-col-title">Shop</h5>
              <ul>
                {shopLinks.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="footer-link">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company links */}
            <div className="footer-links-col">
              <h5 className="footer-col-title">Company</h5>
              <ul>
                {companyLinks.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="footer-link">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Newsletter */}
            <div className="footer-newsletter-col">
              <h5 className="footer-col-title">Tech Updates</h5>
              <p className="footer-nl-text">
                Get tech tips, service offers and product deals straight to your inbox.
              </p>
              {subscribed ? (
                <div className="footer-nl-success">
                  🎉 You're in! Check your inbox.
                </div>
              ) : (
                <form className="footer-nl-form" onSubmit={handleSubscribe}>
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button type="submit" aria-label="Subscribe">
                    <FaArrowRight />
                  </button>
                </form>
              )}

              {/* Payment methods */}
              <div className="footer-payment">
                <p className="footer-payment-label">Secure Payments</p>
                <div className="footer-payment-icons">
                  <FaCcVisa />
                  <FaCcMastercard />
                  <FaCcPaypal />
                  <FaApplePay />
                  <FaGooglePay />
                </div>
              </div>
            </div>
          </div>

          {/* ── Divider ── */}
          <div className="footer-divider" />

          {/* ── Bottom bar ── */}
          <div className="footer-bottom">
            <p className="footer-copy">
              © {new Date().getFullYear()} Follex Tech Kenya. All rights reserved. &nbsp;⚡
            </p>
            <div className="footer-legal">
              <Link to="/privacy">Privacy Policy</Link>
              <span>·</span>
              <Link to="/terms">Terms of Service</Link>
              <span>·</span>
              <Link to="/cookies">Cookies</Link>
            </div>
          </div>

        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
